/**
 * Context-locked palette for the "Drought Conditions" layer (#213, Advisories &
 * Declarations group). Two ingested feeds share the arcgis_public_features `drought`
 * category: GeoBC BC Drought-Level basins (AREA polygons, severity `level-0..5`) and
 * Manitoba river/lake flow-class stations (POINTS, severity a flow-band string like
 * "Much Below Normal").
 *
 * The register is DRYNESS, so the family owns a dry BROWN/TAN ramp (founder spec):
 * pale sandy tan at near-normal deepening to a parched dark brown at the most severe
 * drought. This earth-brown family is distinct from the active-fire hues and the
 * urgent red (Colour Law 5) — drought is a slow background condition, not an alarm.
 * Colour is always paired with the level label at the call site.
 *
 * This module must NOT import another palette family (ESLint no-restricted-imports).
 */
import type { ArcgisLayerStyle } from '../layers/arcgisStyle';

type DroughtTier = ArcgisLayerStyle;

// Dryness ramp, pale → parched. Polygon fills stay ambient (≤0.18).
const RAMP: DroughtTier[] = [
  { color: '#cbb98c', fillAlpha: 0.1, strokeAlpha: 0.7, outlineWidth: 1.5, pointPixelSize: 10, label: 'Normal' },
  { color: '#c2a85f', fillAlpha: 0.12, strokeAlpha: 0.78, outlineWidth: 2, pointPixelSize: 11, label: 'Dry' },
  { color: '#a8843f', fillAlpha: 0.14, strokeAlpha: 0.84, outlineWidth: 2.5, pointPixelSize: 12, label: 'Moderate drought' },
  { color: '#836329', fillAlpha: 0.16, strokeAlpha: 0.88, outlineWidth: 3, pointPixelSize: 13, label: 'Severe drought' },
  { color: '#5e4720', fillAlpha: 0.18, strokeAlpha: 0.92, outlineWidth: 3.5, pointPixelSize: 14, label: 'Extreme drought' },
];

const UNKNOWN: DroughtTier = {
  color: '#9ca3af',
  fillAlpha: 0.1,
  strokeAlpha: 0.7,
  outlineWidth: 1.5,
  pointPixelSize: 10,
  label: 'Drought condition',
};

/** BC GeoBC basins publish `level-0`..`level-5` (5 = most severe). Map onto the ramp
 *  (0 → Normal … 5 → Extreme); the ramp tops out at index 4. */
function bcLevelTier(level: number): DroughtTier {
  // Clamp onto the 5-stop ramp (0 → Normal … 4 → Extreme).
  const idx = Math.max(0, Math.min(level, 4));
  return RAMP[idx]!;
}

/** Manitoba river/lake flow class → dryness. "Below normal" flow is the dry signal;
 *  "above normal" is wet (rendered at the pale baseline — it isn't a drought, but
 *  the feed carries it under the drought monitor). */
function mbClassTier(s: string): DroughtTier {
  if (s.includes('much below')) return RAMP[3]!;
  if (s.includes('below')) return RAMP[2]!;
  if (s.includes('much above') || s.includes('above')) return RAMP[0]!;
  if (s.includes('normal')) return RAMP[0]!;
  return UNKNOWN;
}

/**
 * Resolve the drought style for a feature's free-text severity. Handles both the BC
 * `level-N` scheme and the MB flow-band strings; null/unrecognised → neutral grey.
 */
export function droughtStyle(severity: string | null | undefined): ArcgisLayerStyle {
  const s = (severity ?? '').trim().toLowerCase();
  if (!s) return UNKNOWN;
  const m = /^level-(\d+)$/.exec(s);
  if (m) return bcLevelTier(Number(m[1]));
  return mbClassTier(s);
}
